import * as $ from 'jquery';
import { sortBy, zip } from 'lodash';

import { StraightModel } from '../model/straight.model';
import { TreeModel } from '../model/straight/straight-tree';
import { getTextWidth } from '../util';

const FONT_FAMILY_SANS = '"Graphik", Helvetica, Arial, sans-serif';
const FONT_FAMILY_MONO =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji",  "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"';

/**
 * Dynamic Column Width Plugin
 *
 * Applies table sample widths to header/table cells and
 * creates <colgroup> elements.  When initialized it subscribes
 * to the global window resize event in order to resample
 * and re-apply column widths
 */
export default class DynamicColumnWidth {
  static NAMESPACE = 'axel-dynamic-column-width';
  static COL_MIN_WIDTH = 50;
  static FIRST_COL_MAX_WIDTH = 80;
  static DEFAULT_COL_MAX_WIDTH = 450;

  public $;
  public opts;
  public label;
  private renderer;
  private containerEl;
  private columnWidths: Array<number>;
  private containerBodyWidth = 0;
  public $table;
  private $headerTable;
  public $window;
  public window;

  constructor(renderer, opts = {}) {
    this.$ = $;
    this.opts = opts;
    this.renderer = renderer;
    this.label = 'dynamic-column-width';
    this.columnWidths = [];
  }

  /**
   * [HookMethod] Initialize
   * @param renderer
   * @param containerEl
   * @returns {any}
   */
  public initialize(renderer, containerEl) {
    this.$window = this.$(window);
    this.window = window;
    this.containerEl = containerEl;

    // force fonts to pre-load
    // necessary for column width measurement
    getTextWidth('foobar', FONT_FAMILY_SANS, 12);
    getTextWidth('foobar', FONT_FAMILY_MONO, 12);

    return renderer;
  }

  /**
   * [HookMethod] Build Table
   * @param tableEl
   * @returns {any}
   */
  public buildTable(tableEl) {
    this.$table = this.$(this.resolveTableElement(tableEl));
    this.$headerTable = this.$(this.resolveTableHeaderElement(tableEl));

    // add plugin class
    this.$(tableEl).addClass(DynamicColumnWidth.NAMESPACE);
    this.enableDefaultTableWidth();

    return tableEl;
  }

  /**
   * [HookMethod] After Render
   *
   * After the table renders update the column widths of the
   * header and the table
   */
  public afterRender(tableEl) {
    const resizeHandler = this.updateColumnWidths.bind(this, { override: true });

    this.updateColumnWidths();
    // fix ghost inspector test by sampling again incase
    // fonts failed to pre-load ahead of time
    this.updateColumnWidths({ override: true, delay: 1000 });
    this.$window.on(this.namespace('resize'), resizeHandler);
    return tableEl;
  }

  /**
   * [HookMethod] Destroy
   * @param _
   */
  public destroy(_) {
    this.$window.off(this.namespace('resize'));
  }

  /**
   * Resolve Table Element
   *
   * Retrieve the table element or query the wrapped
   * table element for the main table
   * @param tableEl
   * @returns {Element|any}
   */
  private resolveTableElement(tableEl) {
    return tableEl instanceof HTMLTableElement ? tableEl : tableEl.querySelector('.js-main-table');
  }

  /**
   * Resolve Table Header Element
   * @param tableEl
   * @returns {Element|any}
   */
  private resolveTableHeaderElement(tableEl) {
    return tableEl.querySelector('.js-header-table');
  }

  /**
   * Namespace
   *
   * Retrieve namespaced event label
   * @todo make this a generic helper, maybe implement in a super class
   * @param evtName
   * @returns {string}
   */
  private namespace(evtName) {
    return evtName + '.' + DynamicColumnWidth.NAMESPACE;
  }

  /**
   * Update Column Widths
   *
   * An override-able orchestration method that saves and applies
   * column widths to the DOM
   * @param override
   * @param delay
   */
  private updateColumnWidths({ override = false, delay = 0 } = {}): JQueryDeferred<number> {
    const qualifiesForSave = override === true || !this.columnWidthsAreSaved();
    const loadColumnWidths = (columnWidths: number[]) => {
      const borderOffset = 2;
      const containerWidth = this.containerBodyWidth - borderOffset;

      this.disableDefaultTableWidth();

      if (qualifiesForSave && columnWidths.length) {
        this.saveColumnWidths(columnWidths);
      }
      if (this.columnWidthsAreSaved()) {
        this.updateTableColumnWidths(DynamicColumnWidth.scaleWidths(columnWidths, containerWidth, true));
      }
    };

    return this.loadContainerBodyWidth()
      .done(() => this.getColumnWidths(delay).done(loadColumnWidths))
      .fail(() => {});
  }

  private enableDefaultTableWidth() {
    this.$table.css('width', '100%');
    this.$headerTable.css('width', '100%');
  }

  private disableDefaultTableWidth() {
    this.$table.css('width', 0);
    this.$headerTable.css('width', 0);
  }

  private loadContainerBodyWidth(): JQueryDeferred<number> {
    const deferred: JQueryDeferred<number> = this.$.Deferred();

    requestAnimationFrame(() => {
      this.containerBodyWidth = this.containerEl.firstChild.offsetWidth || this.containerEl.offsetWidth;
      deferred.resolve(this.containerBodyWidth);
    });

    return deferred;
  }

  /**
   * Scale Widths
   *
   * @param columnWidths
   * @param containerWidth
   * @param offsetFirstCell
   * @returns {Int8Array}
   */

  static scaleWidths(columnWidths, containerWidth, offsetFirstCell = false) {
    const tableWidth: number = columnWidths.reduce((a, b) => a + b);
    const firstCellOffset: number = offsetFirstCell ? columnWidths[0] : 0;
    const cellPadding: number = tableWidth > containerWidth ? 16 : 0;
    const ratio = (range, domain, offset) => (range - offset) / (domain - offset);
    const scaleWidth = (columnWidth, ratio) => columnWidth * ratio + cellPadding;

    return columnWidths.map((columnWidth, i) => {
      let scaledWidth: number;

      if (offsetFirstCell === true && i === 0) {
        scaledWidth = columnWidth;
      } else {
        scaledWidth = scaleWidth(columnWidth, ratio(Math.max(tableWidth, containerWidth), tableWidth, firstCellOffset));
      }

      return Math.ceil(scaledWidth);
    });
  }

  private getColumnWidths(delay = 0): JQueryDeferred<number[]> {
    const deferred: JQueryDeferred<number[]> = this.$.Deferred();

    if (this.columnWidthsAreSaved()) {
      deferred.resolve(this.columnWidths);
    } else {
      setTimeout(() => deferred.resolve(this.scanColumnWidths(this.renderer.data())), delay);
    }

    return deferred;
  }

  /**
   *
   * @param {StraightModel} data
   * @returns {number[]}
   */
  public scanColumnWidths(data: StraightModel): number[] {
    const columnNames: string[] = data.cols();
    const columnValuesByColumn: { [colKey: string]: any } = data.selectColumn(columnNames);
    const rowCount: number = data.records.length;
    const tree: TreeModel = data.tree;

    return this.measureColumnWidths(tree, columnValuesByColumn, columnNames, rowCount);
  }

  /**
   *
   * @param {TreeModel} tree
   * @param {[colKey: string]: any} columnValues
   * @param {string[]} columnNames
   * @param {number} rowCount
   * @returns {number[]}
   */
  public measureColumnWidths(
    tree: TreeModel,
    columnValues: { [colKey: string]: any },
    columnNames: string[],
    rowCount: number
  ): number[] {
    const result: number[] = [];
    const canvasElem = this.window.document.createElement('canvas');
    const fontSize = 12;
    const cellPadding = 14;
    const measureHeader = (txt) => getTextWidth(txt, FONT_FAMILY_SANS, fontSize, canvasElem);
    const measureValue = (txt) => getTextWidth(txt, FONT_FAMILY_MONO, fontSize, canvasElem);
    const lengthiestValues: string[] = this.lengthiestColumnValues(tree, columnValues, columnNames);
    const rowCountHeader: string = rowCount.toString();
    const samples: any[][] = [
      [rowCountHeader, ...columnNames],
      ['', ...lengthiestValues],
    ];
    const groupedSamples = zip(...samples);

    for (let i = 0; i < groupedSamples.length; i++) {
      if (measureHeader(groupedSamples[i][0]) > measureValue(groupedSamples[i][1])) {
        result.push(this.applyWidthConstraints(measureHeader(groupedSamples[i][0]) + cellPadding, i));
      } else {
        result.push(this.applyWidthConstraints(measureValue(groupedSamples[i][1]) + cellPadding, i));
      }
    }

    return result;
  }

  /**
   *
   * @param {[colKey: string]: any} columns
   * @param {string[]} colNames
   * @returns any[]
   */
  public lengthiestColumnValues(tree, columns: { [colKey: string]: any[] }, colNames: string[]) {
    const lenComparator = ([value]) => (value || '').toString().length;
    const result: any[] = [];

    for (const colKey of colNames) {
      const column = columns[colKey];

      if (column.length) {
        const sortedCol = sortBy(
          column.map((value, rowKey) => [value, rowKey]),
          lenComparator
        );
        const rowKey = sortedCol.pop()[1];
        const formattedValue = tree[rowKey][colKey].format();
        result.push(formattedValue);
      } else {
        // looks like this column has no values
        // append empty string inplace of undefined
        result.push('');
      }
    }

    return result;
  }

  private columnWidthsAreSaved() {
    return !!this.columnWidths.length;
  }

  private saveColumnWidths(widthArr) {
    this.columnWidths = widthArr;
  }

  private updateTableColumnWidths(widthArr) {
    this.updateColumnGroup(widthArr);
  }

  private applyWidthConstraints(width, i) {
    const cap = (val, min, max) => (val < max ? (val > min ? val : min) : max);
    return i === 0
      ? cap(width, DynamicColumnWidth.COL_MIN_WIDTH, DynamicColumnWidth.FIRST_COL_MAX_WIDTH)
      : cap(width, DynamicColumnWidth.COL_MIN_WIDTH, DynamicColumnWidth.DEFAULT_COL_MAX_WIDTH);
  }

  /**
   * Update Column Group
   *
   * Retrieve (and initialize) table and <colgroup> elements
   * to apply sampled column widths
   * @todo refactor this to only update one colgroup element and copy to header table
   * @param columnWidths
   */
  private updateColumnGroup(columnWidths) {
    const $table = this.$table;
    const $headerTable = this.$headerTable;
    const { $group: $colGroup, $cols } = this.getAndInitColumnGroup($table);
    const { $group: $headerColGroup, $cols: $headerCols } = this.getAndInitColumnGroup($headerTable);

    columnWidths.forEach((width, index) => {
      this.applyAndInitColumnWidth($colGroup, $cols[index], width);
      this.applyAndInitColumnWidth($headerColGroup, $headerCols[index], width);
    });

    this.onColumnWidthUpdate({ $table, containerEl: this.containerEl });
  }

  /**
   * On Column Width Update
   *
   * @param args
   * @returns {any}
   */
  private onColumnWidthUpdate(args: { $table: angular.IAugmentedJQuery; containerEl: Element }) {
    if (this.opts.onColumnWidthUpdate instanceof Function) {
      return this.opts.onColumnWidthUpdate.call(null, args);
    }
  }

  private getAndInitColumnGroup($el) {
    const $group = $el.find('colgroup').length
      ? $el.find('colgroup')
      : $el.prepend(this.$('<colgroup/>')).find('colgroup');
    const $cols = $group.find('col').toArray();
    return { $group, $cols };
  }

  private applyAndInitColumnWidth($colGroup, colEl, width) {
    if (colEl) {
      var $col = this.$(colEl);
    } else {
      var $col = this.$('<col/>');
    }

    if ($col.prop('width') != width) {
      $col.prop('width', width);
    }

    if (!colEl) {
      $colGroup.append($col);
    }
  }
}
